import { forwardRef } from "react";

import {
  Box,
  Pagination as MUIPagination,
  TablePaginationProps,
} from "@mui/material";
import {
  DataGridPro,
  GridPagination,
  gridFilteredTopLevelRowCountSelector,
  gridPageCountSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid-pro";

import { useDataGrid } from "./DataGrid.hooks";
import { DataGridProps, DataGridReference } from "./DataGrid.types";

function Pagination({
  page,
  className,
  onPageChange,
}: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const visibleTopLevelRowCount = useGridSelector(
    apiRef,
    gridFilteredTopLevelRowCountSelector
  );
  const isClientMode = rootProps.paginationMode === "client";

  return (
    <MUIPagination
      showFirstButton
      showLastButton
      color="primary"
      shape="rounded"
      variant="outlined"
      className={className}
      count={
        isClientMode
          ? pageCount
          : Math.ceil(
              (rootProps.rowCount ?? visibleTopLevelRowCount) / pageSize
            )
      }
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(
          event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>,
          newPage - 1
        );
      }}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomPagination(props: any) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

const DataGrid = forwardRef<DataGridReference, DataGridProps>(
  (
    {
      containerProps,
      containerHeight,
      columnHeaderHeight = 44,
      getRowHeight = () => "auto",
      disableRowSelectionOnClick = true,
      pageSizeOptions = [5, 10, 25, 50, 100],
      autoHeight: defaultAutoHeight,
      ...innerProps
    },
    ref
  ) => {
    const { apiRef, autoHeight, dataGridRef } = useDataGrid({
      ref,
      autoHeight: defaultAutoHeight,
      containerHeight,
    });

    return (
      <Box
        height={
          // eslint-disable-next-line
          innerProps.rows &&
          innerProps.rows?.length === 0 &&
          innerProps.loading === false
            ? "400px"
            : autoHeight
            ? undefined
            : containerHeight
        }
        // height={autoHeight ? undefined : containerHeight}
        {...containerProps}
      >
        <DataGridPro
          sx={{
            ".MuiTablePagination-select": {
              paddingRight: "24px !important",
            },
          }}
          {...innerProps}
          ref={dataGridRef}
          apiRef={apiRef}
          autoHeight={
            innerProps.rows &&
            innerProps.rows?.length === 0 &&
            innerProps.loading === false
              ? false
              : autoHeight
          }
          // autoHeight={autoHeight}
          columnHeaderHeight={columnHeaderHeight}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          pageSizeOptions={pageSizeOptions}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            ...innerProps.initialState,
          }}
          getRowHeight={getRowHeight}
          slots={{
            pagination: CustomPagination,
            ...innerProps.slots,
          }}
        />
      </Box>
    );
  }
);

export default DataGrid;
